import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, ArrowLeft, Compass, Heart } from "lucide-react";
import slides from "../data/satMentorSlides";
import Hummingbird from "../components/brand/Hummingbird";

const G = { gold:"#D4AF37", goldSoft:"#EAD08A", violet:"#a48cff", rose:"#E8B4A2", midnight:"#081328" };

const Cover = ({ s }) => (
  <div className="h-full w-full flex flex-col items-center justify-center text-center px-8 lg:px-16 relative">
    <div className="absolute top-10 right-10 opacity-30 hummingbird-float pointer-events-none"><Hummingbird size={140}/></div>
    {s.kicker && <div className="overline mb-6" style={{color:G.gold,letterSpacing:"0.5em"}}>{s.kicker}</div>}
    <h1 className="serif font-light tracking-tight leading-[0.95]" style={{fontSize:"clamp(2.5rem,7vw,7rem)",color:G.gold}}>{s.title}</h1>
    {s.subtitle && <p className="serif italic mt-8 text-xl md:text-2xl lg:text-3xl text-white/75 max-w-3xl">{s.subtitle}</p>}
    {s.tagline && <div className="mt-12 text-[10px] md:text-xs tracking-[0.5em] text-white/55 uppercase font-medium">{s.tagline}</div>}
    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white/30 text-[10px] tracking-[0.4em] uppercase">
      <Heart size={12}/> SAT Mentor · Schweizer Akademie für Transformation
    </div>
  </div>
);

const Content = ({ s }) => (
  <div className="h-full w-full grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 px-8 lg:px-16 py-12 lg:py-16 overflow-y-auto">
    <div>
      {s.kicker && <div className="overline mb-3" style={{color:G.gold}}>{s.kicker}</div>}
      <h2 className="serif text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-3" style={{color:G.gold}}>{s.title}</h2>
      {s.subtitle && <p className="serif italic text-white/65 text-lg mb-8">{s.subtitle}</p>}
      <ul className="space-y-5">
        {s.bullets?.map((b,i)=>(
          <li key={i} className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full border flex items-center justify-center text-xs font-semibold mt-0.5" style={{borderColor:G.gold,color:G.gold}}>{String(i+1).padStart(2,"0")}</div>
            <div>
              <div className="font-semibold tracking-wide uppercase text-sm" style={{color:G.gold}}>{b.h}</div>
              {b.t && <div className="text-white/70 text-sm md:text-[15px] leading-relaxed mt-1">{b.t}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
    <div className="flex flex-col gap-6 justify-center">
      {s.mantra && (
        <div className="relative rounded-3xl p-8 lg:p-10 border" style={{borderColor:`${G.gold}55`,background:"rgba(212,175,55,0.04)"}}>
          <div className="text-3xl absolute top-3 left-5" style={{color:G.gold}}>&ldquo;</div>
          <p className="serif italic text-xl md:text-2xl text-white/85 leading-snug">{s.mantra}</p>
          {s.mantraSub && <p className="text-white/55 text-sm mt-4 italic">{s.mantraSub}</p>}
        </div>
      )}
      {s.side && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7">
          <div className="text-xs uppercase tracking-[0.3em] font-semibold mb-5" style={{color:G.gold}}>{s.side.title}</div>
          <ul className="space-y-3">
            {s.side.items.map((it,i)=><li key={i} className="text-white/75 text-sm flex gap-3"><span style={{color:G.gold}}>◆</span>{it}</li>)}
          </ul>
        </div>
      )}
    </div>
  </div>
);

const Finale = ({ s }) => (
  <div className="h-full w-full grid lg:grid-cols-[1fr_1fr] gap-12 px-8 lg:px-16 py-12 overflow-y-auto">
    <div>
      {s.kicker && <div className="overline" style={{color:G.gold}}>{s.kicker}</div>}
      <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light mt-2" style={{color:G.gold}}>{s.title}</h2>
      <p className="serif italic text-white/75 text-lg mt-4">{s.subtitle}</p>
      <ul className="space-y-4 mt-8">
        {s.bullets?.map((b,i)=>(
          <li key={i}>
            <div className="font-semibold uppercase text-xs tracking-wider" style={{color:G.gold}}>{b.h}</div>
            <div className="text-white/70 text-sm mt-1 leading-relaxed">{b.t}</div>
          </li>
        ))}
      </ul>
    </div>
    <div className="flex flex-col gap-6 justify-center">
      {s.formula && (
        <div className="rounded-2xl border p-7" style={{borderColor:`${G.gold}55`,background:"rgba(212,175,55,0.05)"}}>
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-4" style={{color:G.gold}}>Die Formel</div>
          <div className="space-y-3">
            {s.formula.map((f,i)=><div key={i} className="text-xl serif" style={{color:i===s.formula.length-1?G.gold:"rgba(255,255,255,0.85)"}}>{f}</div>)}
          </div>
        </div>
      )}
      {s.mantra && (
        <div className="relative rounded-2xl p-8 border" style={{borderColor:`${G.gold}55`,background:"rgba(212,175,55,0.04)"}}>
          <p className="serif italic text-xl text-white/90 leading-snug">&ldquo;{s.mantra}&rdquo;</p>
          {s.mantraSub && <p className="text-sm mt-4 font-semibold tracking-wide" style={{color:G.gold}}>{s.mantraSub}</p>}
        </div>
      )}
    </div>
  </div>
);

const render = (s) => {
  if (s.type==="cover") return <Cover s={s}/>;
  if (s.type==="finale") return <Finale s={s}/>;
  return <Content s={s}/>;
};

export default function SatMentorDeck() {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const total = slides.length;
  const go = useCallback((d)=>setIdx(i=>Math.min(total-1,Math.max(0,i+d))),[total]);

  useEffect(()=>{
    const k=(e)=>{
      if(e.key==="ArrowRight"||e.key===" ")go(1);
      else if(e.key==="ArrowLeft")go(-1);
      else if(e.key==="f")setFs(f=>!f);
      else if(e.key==="Escape")setFs(false);
      else if(e.key==="Home")setIdx(0);
      else if(e.key==="End")setIdx(total-1);
    };
    window.addEventListener("keydown",k);
    return ()=>window.removeEventListener("keydown",k);
  },[go,total]);

  const slide = slides[idx];

  return (
    <div className={`${fs?"fixed inset-0 z-[100]":"min-h-screen pt-24 pb-12"} bg-[#0E1A30] text-white`} data-testid="sat-mentor-deck">
      <div className={`${fs?"h-screen":""} container-sat`}>
        {!fs && (
          <div className="flex items-center justify-between mb-6">
            <Link to="/mentor" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase hover:translate-x-[-2px] transition" style={{color:G.gold}} data-testid="mentor-deck-back">
              <ArrowLeft size={12}/> Zurück
            </Link>
            <div className="text-[10px] tracking-[0.35em] uppercase text-white/45">SAT Mentor · Programm-Übersicht</div>
            <button onClick={()=>setFs(true)} className="text-white/55 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-wider" data-testid="mentor-deck-fullscreen-on">
              <Maximize2 size={14}/> Vollbild
            </button>
          </div>
        )}

        <div className={`relative ${fs?"h-full":"h-[78vh] min-h-[640px]"} rounded-2xl overflow-hidden`}
             style={{background:"linear-gradient(135deg, #0E1A30 0%, #050B1B 100%)",border:fs?"none":"1px solid rgba(212,175,55,0.18)"}}
             data-testid={`mentor-slide-${slide.num}`}>
          <div className="absolute top-5 left-6 flex items-center gap-3 z-10">
            <div className="w-8 h-8 rounded-full border flex items-center justify-center" style={{borderColor:G.gold}}>
              <Heart size={14} style={{color:G.gold}}/>
            </div>
            <div className="leading-tight">
              <div className="serif text-sm" style={{color:G.gold}}>SAT Mentor</div>
              <div className="text-[8px] tracking-[0.3em] uppercase text-white/50">Schweizer Akademie</div>
            </div>
          </div>
          <div className="absolute top-5 right-6 text-xs tracking-[0.3em] uppercase font-light z-10" style={{color:G.gold}}>
            {String(slide.num).padStart(2,"0")} / {String(total).padStart(2,"0")}
          </div>

          <div className="h-full pt-16 pb-12">{render(slide)}</div>

          <div className="absolute bottom-3 left-0 right-0 text-center text-[9px] tracking-[0.35em] uppercase text-white/35">
            SAT — Schweizer Akademie für Transformation · Nenad Vladovic
          </div>

          {fs && (
            <button onClick={()=>setFs(false)} className="absolute top-5 right-1/2 translate-x-1/2 text-white/55 hover:text-[#D4AF37] flex items-center gap-2 text-xs uppercase tracking-wider z-20" data-testid="mentor-deck-fullscreen-off">
              <Minimize2 size={14}/> Schließen (Esc)
            </button>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button onClick={()=>go(-1)} disabled={idx===0}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition disabled:opacity-30"
                  data-testid="mentor-deck-prev">
            <ChevronLeft size={16}/> Vorherige
          </button>
          <div className="hidden md:flex items-center gap-1.5 max-w-2xl overflow-x-auto">
            {slides.map((_,i)=>(
              <button key={i} onClick={()=>setIdx(i)}
                      className={`shrink-0 transition-all rounded-full ${i===idx?"w-6 h-2 bg-[#D4AF37]":"w-2 h-2 bg-white/20 hover:bg-white/40"}`}
                      aria-label={`Slide ${i+1}`}
                      data-testid={`mentor-deck-dot-${i}`}/>
            ))}
          </div>
          <button onClick={()=>go(1)} disabled={idx===total-1}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-30"
                  style={{background:G.gold,color:G.midnight}}
                  data-testid="mentor-deck-next">
            Nächste <ChevronRight size={16}/>
          </button>
        </div>

        {!fs && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase text-white/30">
            Pfeiltasten ◄ ► · Leertaste · F für Vollbild · Esc zum Schließen
          </div>
        )}
      </div>
    </div>
  );
}
